import { createSelector } from 'reselect';
import { getSeriesOption } from '../views/series/selectors/eventSeries';
import { hasSelectedChildEvent } from '../views/series/selectors/eventSeries';
import {EMBED_CART_BEHAVIOR_HIDE, EMBED_TYPE_MULTI, TICKET_FEE_SEPARATE, TICKET_PRICE_ONLY, TICKETS_DISPLAY_LIST} from '../reducers/ui';
import { POSITION_EMBEDDED_IN_REGISTRATION } from '../views/upsells/reducers/upsells';
import { isPyos, isSeatedListing, showPyosPicker, hasAppliedReserveCoupon } from './reserved-seating';
import { EMBED_CART_BEHAVIOR_PIN_TO_TOP, EMBED_CART_BEHAVIOR_PIN_TO_BOTTOM } from '../reducers/ui';
import { areAllQuestionsCompleted } from './questions';
import { isDynamicListing } from './dynamic-listing';
import { getMainEvent, isSeries } from '../../checkout/selectors/event';

const MAX_PROTECTED_DAYS = 730;

const getSteps = state => Array.isArray(state.ui.listingConfig?.checkoutSteps) ? state.ui.listingConfig.checkoutSteps : [];
const getCurrentStep = state => state.ui.currentStep;
export const getCaptureMethod = state => state.payment.method;
const getPurchaseUser = state => state.reservation.purchaseUser;
const getTerminalId = state => state.payment.terminalId;
const getTerminalConnection = state => state.payment.isReaderConnected;
const seriesConfig = state => state.ui.checkoutConfig.seriesOptions;
export const getListingConfig = state => state.ui && state.ui.listingConfig ? state.ui.listingConfig : {};
export const getMembershipFormConfig = state => state.ui?.uiConfig?.membershipFormConfig;
const getUiConfig = state => state.ui.uiConfig;
const getCheckoutConfig = state => state.ui && state.ui.checkoutConfig ? state.ui.checkoutConfig : {};
const isCalendarOnListing = state => getSeriesOption("calendarOnListing", false)(state);
const getRouter = state => state.router;
export const getCurrentRouteName = state => state.ui.currentRouteName;
const getAccessInventory = state => state.coupon.accessInventory;
const getEvent = state => state.event;
const getAllState = state => state;
const getUIState = state => state.ui;
const getIsEmbed = state => !!state.ui.isEmbed;
const getTickets = state => state.tickets.tickets;
const getProductSales = state => state.productSales.products;
const getBundleSales = state => state.bundleSales.bundles;
const getInsuranceProvider = state => state.insurance.provider;
export const getEmbedCartBehavior = state => state.ui?.uiConfig?.listings?.embedCartBehavior;
export const reservationCompleted = state => state.reservation.isCompleted === true;
const overlay = state => state.ui.overlay;
export const hasReservation = state => state.reservation.token !== "";
export const getIsDevMode = state => !!state.ui.isDevMode;
const isMembershipSkipped = state => state.ui.skipMembership;
const getIsDynamicListing = state => isDynamicListing(state);
export const getDynamicListingSlug = state => state.dynamicListing.listingSlug;
const getForceLogin = state => state.ui.forceLogin;
const getIsMemberLoggedIn = state => state.ui.hasMembershipContext;
const getAllProps = (_, props) => props;
const isBestAvailableOrPyosPicker = state => isPyos(state) && !state.ui.showBestAvailablePicker && !state.ui.showPyosPicker && !hasAppliedReserveCoupon(state);

const getTotalCost = state => state.reservation.total_cost.amount || 0;
export const getCollectAddressesOnFree = state => state.ui.collectAddressesOnFree;

export const getIsMailDelivery = state => {
  const deliveryOptions = state.delivery?.options || [];
  const reservation = state.reservation || {};
  const selectedDeliveryOption = deliveryOptions.find((option) => option.id === reservation.selected_delivery_option) || { method_name: null }
  return selectedDeliveryOption.method_name === 'Mail';
}

export const selectIsHeroLayout = createSelector(getUiConfig, config => config.layout === 'hero');

export const selectIsPosterLayout = createSelector(getUiConfig, config => config.layout === 'poster');

export const showStatusBar = (isEmbeddedPicker = false) => {
    return createSelector(
        [getAllState],
        (allState) => {
            const ticketCount = allState.reservation?.ticket_count ? +allState.reservation.ticket_count : 0;
            return !isEmbeddedPicker && (ticketCount >= 1 || hasAppliedReserveCoupon(allState)) && !showPyosPicker(allState);
        }
    )
}

export const hasOverMaxProtectedDays = createSelector(
  [getTickets, getProductSales, getBundleSales, getInsuranceProvider],
  (tickets, productSales, bundleSales, provider) => {
    if (provider !== 'protect') {
      return false;
    }

    if(!tickets.length && !productSales.length && !bundleSales) {
      return true;
    }

    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + MAX_PROTECTED_DAYS);

    let isOverMax = tickets.some(ticket => {
      const ticketDate = new Date(ticket.event_start);
      return ticketDate >= maxDate;
    })
    if(isOverMax) {
      return true;
    }
    isOverMax = productSales.some(productSale => {

      const ticketDate = new Date(productSale.event_start);
      return ticketDate >= maxDate;
    })
    if(isOverMax) {
      return true;
    }
    isOverMax = bundleSales.some(bundleSale => {
      const ticketDate = new Date(bundleSale.event_start);
      return ticketDate >= maxDate;
    })
    if(isOverMax) {
      return true;
    }
  }
)

export const isMultiEmbed = createSelector(
  [getUIState],
  (ui) => !!ui.isEmbed && ui.embedType === EMBED_TYPE_MULTI
)

export const allStepsComplete = createSelector(
    [getListingConfig, getTotalCost , getCollectAddressesOnFree, getIsMailDelivery],
    (config, totalCost, collectAddressesOnFree, isMailDelivery) => {
      const ignoreAddress = totalCost == 0  &&  collectAddressesOnFree === false && !isMailDelivery;
      const relevantSteps = ignoreAddress
        ? config.checkoutSteps.filter(step => step.title !== 'Address')
        : config.checkoutSteps;
      return relevantSteps.every(step => step.complete === true);
    }
)

export const getCurrentCheckoutStep = createSelector(
    [getSteps, getCurrentStep],
    (steps, currentStepIndex) => {
        return Array.isArray(steps) && steps.length > currentStepIndex ? steps[currentStepIndex] : null;
    }
)

export const inLastStep = createSelector(
    [getSteps, getCurrentStep],
    (steps, currentStepIndex) => {
        return Array.isArray(steps) && currentStepIndex === steps.length - 1;
    }
)

export const getIsInLastStep = createSelector(
    [getSteps],
    (steps) => {
        return (route) => {
          if (!route || !route.startsWith('checkout-')) {
            return;
          }

          const stepIndex = parseInt(route.replace('checkout-', ''));

          return Array.isArray(steps) && stepIndex === steps.length;
        };
    }
)

export const getBlockingErrors = createSelector(
    [
        getCaptureMethod,
        getPurchaseUser,
        getTerminalId,
        getTerminalConnection,
        getListingConfig,
        getIsDevMode,
    ],
    (
        method,
        purchaseUser,
        terminalId,
        terminalConnected,
        config,
        isDevMode
    ) => {
        let errors = [];
        // corresponds to CAPTURE_KIOSK
        if(method === 13) {
            let cmsMode = getCmsModeFromConfig(config);
            if(!cmsMode) {
                if (!purchaseUser || purchaseUser == 0) {
                    errors.push("checkout__kiosk_user_must_be_set");
                }

                if(!isDevMode) {
                    if (+terminalId === 0) {
                        errors.push("checkout__no_emv_terminal_selected");
                    } else if (!terminalConnected) {
                        errors.push("checkout__emv_terminal_is_disconnected");
                    }
                }
            }
        }

        return errors;
    }
);

export const isKioskMode = createSelector(
    [
        getCaptureMethod,
    ],
    (
        captureMethod
    ) => {
        return isCaptureMethodKioskMode(captureMethod);
    }
);

export const isAlwaysShowHiddenLevels = createSelector(
    [getCheckoutConfig],
    (config) => config?.alwaysShowHiddenLevels
);

const isCaptureMethodKioskMode = captureMethod => captureMethod === 13;

export const getSeriesConfig = createSelector(
    [
        seriesConfig,
    ],
    (
        config
    ) => {
        if(config) {
            return config;
        }

        return {};
    }
);

export const getListingSettings = createSelector(
    [
        getListingConfig,
        getMembershipFormConfig,
        isCalendarOnListing,
        getIsEmbed,
        (state) => state.reservation,
        isMembershipSkipped,
        getForceLogin,
        getIsMemberLoggedIn,
        selectIsHeroLayout,
        getMainEvent,
        (state) => state.priceLevel.levels,
    ],
    (
        config,
        membershipFormConfig,
        calendarOnListing,
        isEmbed,
        reservation,
        skipMembership,
        isForceLogin,
        isMemberLoggedIn,
        isHero,
        mainEvent,
        priceLevels,
    ) => {
        let settings = {};

        const { isOnsale, hasPresale } = mainEvent?.event || {}

        if (!isEmbed) {
            if (membershipFormConfig?.membership_available
              && !reservation.hasMembershipInstance
              && isForceLogin
              && !isMemberLoggedIn){
              return getListingSettingsMemberLogin();
            }

            if (membershipFormConfig?.membership_available &&
              !reservation.hasMembershipInstance &&
              !skipMembership && !isMemberLoggedIn &&
              (membershipFormConfig?.membership_required || !membershipFormConfig?.skip_login)
            ) {
                return getListingSettingsMemberLogin();
            }

            if(config && config.listing) {
                settings = config.listing;
            } else if (calendarOnListing) {
                settings = getListingSettingsWithCalendar(isHero);
            } else {
                settings = getDefaultListingSettings(config, {
                  isOnsale,
                  hasPresale,
                  hasPriceLevels: Object.keys(priceLevels || {}).length > 0,
                });
            }
        }

        return settings;
    }
);

const buildInventoryFooterContainerComponents = (includeMemberAuthButtons = true) => {
    const invFooterResult = {
        name: 'InventoryFooterContainer',
        children: [
            {
                name: 'InventoryErrors',
            },
            {
                name: 'FeeDisclosure',
            },
            {
                name: 'CheckoutButton',
            },
            {
                name: "CouponEntryButton",
            },
        ],
    };
    if (includeMemberAuthButtons) {
        invFooterResult.children.push({
            name: 'memberAuthButtons',
        });
    }
    return invFooterResult;
}

function getDefaultListingSettings(config, { isOnsale, hasPresale, hasPriceLevels }) {
    if(config.layout === "kiosk") {
        return {
            components: [
                {
                    name: 'continueCheckoutBanner',
                },
                {
                    name: 'KioskListing',
                    children: [
                        {
                            name: "SoldOutDayDisplay",
                        },
                        {
                        name: 'ticketsButton',
                            props: {type: 'banner__touch'},
                            children: [
                                {
                                    name: "NextAvailableTime",
                                },
                            ]
                        },
                        {
                            name: "TicketsGoOnSale",
                        }
                    ]
                },
                {
                    name: "KioskManagement",
                },
                {
                    name: "KioskMonitoring",
                    props: {
                        // 'interval' => 1000 // in ms
                    }
                },
                {
                    name: "TimeClock",
                },
                {
                    name: "RefreshListingData",
                },
                {
                    name: "KioskTimeContainer",
                },
                {
                    name: "TimeoutPromptContainer",
                },
            ]
        }
    }

    const ticketPickerChild = {
        name: "TicketPickerContainer",
        props: {
          inline: true,

          // These props are needed for listing/components/PageContentWithTabs.js
          // We should not display TicketPicker in the tickets section on desktop if its Poster layout
          tabTickets: true,
          hideOnDesktop: config?.layout === 'poster',
        },
        children: [
            {
                name: 'CouponAlertsContainer',
            },
            {
                name: 'AlertContainer',
            },
            {
                name: 'InventoryContentContainer',
                children: [
                    {
                        name: 'InventoryItemsContainer',
                    },
                ]
            },
            buildInventoryFooterContainerComponents(),
        ],
    };

    const canShowTicketPicker =
      config?.listings?.ticketsDisplayType === TICKETS_DISPLAY_LIST &&
      (isOnsale || hasPriceLevels);

    const listingBannerChild =
      canShowTicketPicker
      ? [ticketPickerChild]
      : [(isOnsale || (!isOnsale && hasPresale)) && { name: 'ticketsButton' }, { name: 'memberAuthButtons' }].filter(Boolean);

    const listingEventDetailsSection = {
        name: 'ListingEventDetails',
        props: {
            // necessary for PageContentWithTabs
            withPageWrapper: config?.layout !== 'poster',
        },
    };

    const listingDetailsSection = {
        name: 'listingDetails',
        props: {
            // necessary for PageContentWithTabs
            tabContent: true,
        },
    };

    const contactInfoSection = {
        name: 'contactInfo',
        props: {
            // necessary for PageContentWithTabs
            tabContent: true,
        },
    };

    const footerSection = {
        name: 'footer',
        props: {
            // necessary for PageContentWithTabs
            tabFooter: true,
        },
    };

    const continueCheckoutBannerSection = {
        name: 'continueCheckoutBanner',
    };

    if (config?.layout !== 'banner' && config?.listings?.ticketsDisplayType === TICKETS_DISPLAY_LIST) {
        const pagePosterChildren = [
            listingEventDetailsSection,
            config?.layout === 'poster' && ticketPickerChild,
            listingDetailsSection,
            contactInfoSection,
            footerSection,
        ];

        const pageHeroChildren = [
            listingEventDetailsSection,
            listingDetailsSection,
            contactInfoSection,
            config?.layout === 'hero' && ticketPickerChild,
            footerSection,
        ];

        return {
            components: [
                continueCheckoutBannerSection,
                {
                    name: 'listingBanner',
                },
                {
                    name: 'PageContentWithTabs',
                    children: config?.layout === 'poster' ? pagePosterChildren : pageHeroChildren,
                }
            ].filter(Boolean),
        };
    }

    return {
        components: [
            continueCheckoutBannerSection,
            {
                name: 'listingBanner',
                children: listingBannerChild
            },
            config?.listings?.ticketsDisplayType === TICKETS_DISPLAY_LIST && ticketPickerChild,
            listingDetailsSection,
            contactInfoSection,
            footerSection,
        ].filter(Boolean),
    }
}

function getListingSettingsWithCalendar(isHero) {
    return {
        components: [
            {
                name: 'continueCheckoutBanner',
            },
            {
                name: 'listingBanner',
                children: [
                    {
                        name: 'listingDatePicker',
                        props: {
                            showSelectDateLabel: isHero
                        }
                    },
                    {
                        name: 'memberAuthButtons'
                    }
                ]
            },
            {
                name: 'listingDetails',
            },
            {
                name: 'contactInfo',
            },
            {
                name: 'footer',
            },
        ],
    };
}

export const getListingSettingsMemberLogin = () => {
  return {
    components: [
      {
        name: 'listingBanner',
        props: {
          bannerImageOnly: true,
        }
      },
      {
        name: 'MemberLogin',
      },
      {
        name: 'footer',
      }
    ]
  }
}

const getBestAvailableOrPyosTicketPicker = (
    children = [],
    props = { focusTrap: true, isEmbeddedPicker: true, tabTickets: true },
) => {
    return {
        name: "TicketPickerContainer",
        props,
        children,
    };
};

export const getCheckoutStepsSettings = createSelector(
    [
        state => ((state.ui?.listingConfig || {}).checkoutSteps) || {},
        getCurrentStep,
    ],
    (
        checkoutSteps,
        currentStep,
    ) => {
        let settings = {};
        if(checkoutSteps) {
            settings = checkoutSteps[currentStep];
        } else {
            const steps = getDefaultCheckoutStepSettings();
            settings = steps[currentStep];
        }

        return settings;
    }
);

export function getDefaultCheckoutStepSettings(
  config,
  {
    hasQuestions,
    hasDeliveryOptions,
    requiresNameOnTicket,
    isFree,
    collectAddressesOnFree
  } = {}
) {
  if (config.layout === "kiosk") {
    let checkoutSteps = [];
    const kioskOptions = getKioskOptionsFromConfig(config);
    if (kioskOptions.collectCustomerName || kioskOptions.collectCustomerEmail || hasQuestions || hasDeliveryOptions) {
        checkoutSteps.push({
            title: 'Registration',
            components: [
                {
                    name: 'memberInfo',
                },
                {
                    name: 'nameOnTicket',
                },
                {
                    name: 'delivery',
                },
                {
                    name: 'upsells',
                    props: {
                        position: POSITION_EMBEDDED_IN_REGISTRATION,
                    }
                },
                {
                    name: 'questions',
                },
            ],
        });
    }
    if (!isFree) {
        let step = {
            title: "Payment",
            components: [
                {
                    'name': "TimeoutPromptContainer"
                },
                {
                    'name': "StripeEmvPayment"
                }
            ],
        };
        checkoutSteps.push(step);
    }
    return checkoutSteps;
} else {
    const additionalInfoComponents = [
      {
        name: 'nameOnTicket',
      },
      {
        name: 'questions',
      },
    ];

    const showSeperateAdditionalInfo = hasQuestions || requiresNameOnTicket;

    let steps = [
      {
        title: 'Registration',
        components: [
          {
            name: 'memberInfo',
          },
          ...(!showSeperateAdditionalInfo ? additionalInfoComponents : []),
          {
            name: 'delivery',
          },
          {
            name: 'donation',
          },
          {
            name: 'upsells',
            props: {
              position: POSITION_EMBEDDED_IN_REGISTRATION,
            },
          },
          {
            name: 'weather',
          },
        ],
      },
      showSeperateAdditionalInfo && {
        title: 'Additional Info',
        components: additionalInfoComponents,
      },
    ].filter(Boolean);

    if (isFree) {
      if (collectAddressesOnFree) {
        steps.push({
          title: 'Address',
          components: [
            {
              name: 'address',
            },
          ],
        });
      }
    } else {
      steps.push({
        title: 'Payment',
        components: [
          {
            name: 'payment',
          },
        ],
      });
    }

    return steps;
  }
}

export const getTicketPickerSettings = (isEmbeddedPicker = false) => {
    return createSelector(
        [
            getListingConfig,
            getEvent,
            getAllState,
        ],
        (
            config,
            event,
            allState
        ) => {
            //if we ever make it to this state, render an empty, but recoverable ticketpicker
            if (Object.values(event.events)[0]?.event.isRecurring && !hasSelectedChildEvent({event})) {
                return {
                    components: [
                        {
                            name: "TicketPickerContainer",
                            children: [
                                {
                                    name: 'InventoryHeaderContainer',
                                },
                                {
                                    name: 'InventoryFooterContainer',
                                }
                            ],
                        },
                    ],
                };
            }
            let settings = {};
            if(config && config.ticketPicker) {
                settings = config.ticketPicker;
            } else {
                settings = getDefaultTicketPickerSettings(config, isEmbeddedPicker, allState);
            }

            return settings;
        }
    );
}

export const getPickerWithDetailsSettings = createSelector(
  [getAllState],
  (state) => {
    const bannerImage = state.ui.uiConfig?.images?.embed_image ?? '';

    const isSeated = isSeatedListing(state);

    const commonActions = [
      {
        name: "CouponEntryButton",
      },
      {
        name: "ContinueShoppingButton"
      }
    ]

    const regularTicketPickerActions = [
      {
        name: 'CheckoutButton'
      },
    ];
    const seatedAcions = [
      {
        name: 'BestAvailableOrPyosPicker'
      }
    ];

    const pickerActionsChildren = isSeated ? seatedAcions : regularTicketPickerActions;

    return {
      components: [
        {
          name: 'PickerWithDetailsMain',
          children: [
            !isSeated && {
              name: 'InventoryContentContainer',
              children: [
                {
                    name: 'AlertContainer',
                },
                {
                  name: 'InventoryItemsContainer'
                },
              ]
            },
            {
              name: 'InventoryErrors'
            },
            {
              name: 'PickerWithDetailsActions',
              children: [
                ...pickerActionsChildren,
                ...commonActions
              ]
            }
          ].filter(Boolean),
          props: {
            bannerImage
          }
        }
      ]
    }
  })

const getSeatedTicketPickerConfig = (InventoryHeaderComponent, containerProps) => {
    return {
        components: [
            {
                name: "TicketPickerContainer",
                props: containerProps,
                children: [
                    {
                        name: 'CouponAlertsContainer',
                    },
                    InventoryHeaderComponent,
                    {
                        name: 'AlertContainer',
                    },
                    {
                        name: 'SeatingChartTicketPickerContainer',
                    },
                ],
            },
        ],
    };
}

export const getDynamicListingSettings = createSelector(
  [getAllState, isBestAvailableOrPyosPicker, isSeries],
  (state, isBestAvailablePicker, isSeriesEvent) => {

    // Prevent errors if event is empty or not found
    if (!getMainEvent(state)) {
        return {
            components: [],
        };
    }

    const isSeated = isSeatedListing(state);

    const InventoryHeaderComponent = {
        name: 'InventoryHeaderContainer',
        props: {
            hide: isSeated && !showPyosPicker(state),
        }
    };
    
    // either the user selected PYOS or this listing only supports PYOS
    if (isSeated && showPyosPicker(state)) {
        return getSeatedTicketPickerConfig(InventoryHeaderComponent, {
            focusTrap: false,
            isSeatingChart: true,
            isEmbeddedPicker: true,
            tabTickets: true, // necessary for PageContentWithTabs
        });
    }

    const ticketPickerChild = {
        name: "TicketPickerContainer",
        props: {
          inline: true,
          tabTickets: true, // necessary for PageContentWithTabs
          focusTrap: false
        },
        children: [
            {
                name: 'CouponAlertsContainer',
            },
            {
                name: 'AlertContainer',
            },
            {
                name: 'InventoryContentContainer',
                children: [
                    {
                        name: 'InventoryItemsContainer',
                    },
                ]
            },
            buildInventoryFooterContainerComponents(),
        ],
    };

    const seriesTicketPickerChild = {
        name: "SeriesDatePicker",
        props: {
          tabTickets: true, // necessary for PageContentWithTabs
          focusTrap: false
        },
    };

    const singleRsTicketPickerChild = getBestAvailableOrPyosTicketPicker(
        [
            {
                name: 'DynamicTicketsHeader',
            },
            InventoryHeaderComponent,
            {
                name: 'BestAvailableOrPyosPicker',
            },
        ],
        {
            focusTrap: false,
            isEmbeddedPicker: true,
            tabTickets: true, // necessary for PageContentWithTabs
        }
    );

    const singleEventTicketPicker = isSeated && isBestAvailablePicker
        ? singleRsTicketPickerChild
        : ticketPickerChild;

    const seriesEventTicketPicker = isSeated && !isBestAvailablePicker ? ticketPickerChild : seriesTicketPickerChild;

    const ticketPicker = isSeriesEvent ? seriesEventTicketPicker : singleEventTicketPicker;

    return {
        components: [
            {
                name: 'listingBanner',
            },
            {
                name: 'PageContentWithTabs',
                children: [
                    {
                        name: 'ListingEventDetails',
                        props: {
                            withPageWrapper: true,
                        },
                    },
                    {
                        name: 'listingDetails',
                        props: {
                            // necessary for PageContentWithTabs
                            tabContent: true,
                        },
                    },
                    {
                        name: 'VenueInfo',
                        props: {
                            // necessary for PageContentWithTabs
                            tabContent: true,
                        },
                    },
                    {
                        name: 'contactInfo',
                        props: {
                             // necessary for PageContentWithTabs
                            tabContent: true,
                        },
                    },
                    ticketPicker
                ],
            },
            {
                name: 'CustomerCareHeader',
                props: {
                    ...state?.ui?.uiConfig?.customerCare,
                },
            },
        ].filter(Boolean),
    };
  })

function getDefaultTicketPickerSettings(config, isEmbeddedPicker, allState) {
    const InventoryHeaderComponent = {
        name: 'InventoryHeaderContainer',
        props: {
            hide: !!isEmbeddedPicker,
        }
    };

    if (config.layout === 'kiosk') {
        return {
            components: [
                {
                    name:"KioskLayoutContainer",
                    props:{
                        splitPane: true,
                        couponAlerts: true,
                        inventoryErrors: true,
                    },
                    children: [
                        {
                            name: 'InventoryContentContainer',
                            children: [
                                {
                                    name: 'InventoryItemsContainer',
                                    children: [
                                        {
                                            name: 'PriceLevelWrapper',
                                            // not supporting reserved seating in kiosk for now
                                            // props: {
                                            //     showBestAvailablePicker: showBestAvailablePicker(allState),
                                            // },
                                            children: [
                                                {
                                                    name: 'ChangeDateContainer',
                                                }
                                            ]
                                        },
                                    ]
                                },
                            ]
                        },
                    ],
                }
            ],
        };
    }

    const isSeated = isSeatedListing(allState);

    if (isSeated) {
        // if this listing supports best available and pyos, and the user has not selected one
        // show a view where the user can select which flow they want to follow
        if (isBestAvailableOrPyosPicker(allState)) {
            return {
                components: [getBestAvailableOrPyosTicketPicker(
                    [
                        InventoryHeaderComponent,
                        {
                            name: 'BestAvailableOrPyosPicker',
                        },
                    ],
                    {
                        focusTrap: true,
                        isEmbeddedPicker,
                    },
                )],
            };
        }

        // either the user selected PYOS or this listing only supports PYOS
        if (showPyosPicker(allState)) {
            return getSeatedTicketPickerConfig(InventoryHeaderComponent, {
                focusTrap: true,
                isSeatingChart: true,
                isEmbeddedPicker,
            });
        }
    }

    return {
        components: [
            {
                name: "TicketPickerContainer",
                props: {
                    isEmbeddedPicker,
                },
                children: [
                    {
                        name: 'CouponAlertsContainer',
                    },
                    InventoryHeaderComponent,
                    {
                        name: 'AlertContainer',
                    },
                    {
                        name: 'InventoryContentContainer',
                        children: [
                            {
                                name: 'ChangeDateContainer'
                            },
                            {
                                name: 'InventoryItemsContainer',
                            },
                        ]
                    },
                    buildInventoryFooterContainerComponents(false),
                ],
            },
        ],
    };
}

export const getCustomInventory = createSelector(
    [
        getCheckoutConfig,
        getAccessInventory,
    ],
    (
        checkoutConfig,
        accessInventory
    ) => {
        if (accessInventory) {
            return accessInventory;
        }
        return checkoutConfig.inventory?.length ? checkoutConfig.inventory : null;
    }
);

export const getConfirmationPageSettings = createSelector(
    [
        getListingConfig,
    ],
    (
        config,
    ) => {
        let settings = {};
        if(config && config.confirmation) {
            settings = config.confirmation;
        } else {
            return getDefaultConfirmationSettings(config);
        }

        return settings;
    }
);

function getDefaultConfirmationSettings(config) {
    if(config.layout === "kiosk") {
        return {
            components: [
                {
                    'name': "ConfirmationHeader",
                },
                {
                    'name': "KioskConfirmationContainer",
                },
                {
                    'name': "KioskGoBackButton",
                },
                {
                    'name': "GoBackTimeout",
                },
                {
                    'name': "SidekickTicketPrint",
                },
                {
                    'name': "KioskFooterContainer"
                }
            ],
        };
    } else {
        return {
            components: [
                {
                    name: 'ConfirmationContainer',
                },
            ]
        };
    }
}

export const getConfirmationConfig = createSelector([getUiConfig], (uiConfig) => {
    let config = {};

    if(uiConfig && uiConfig.checkout && uiConfig.checkout.confirmation) {
        config = uiConfig.checkout.confirmation;
    }

    return config;
});

export function getKioskOptionsFromConfig(config) {
    return config.checkout && config.checkout.kioskOptions ? config.checkout.kioskOptions : {};
}

export const getKioskOptions = createSelector(
    [
        getListingConfig,
    ],
    (
        config,
    ) => {
        return getKioskOptionsFromConfig(config);
    }
);

function getCmsModeFromConfig(config) {
    return config.checkout && config.checkout.cmsMode ? config.checkout.cmsMode : false;
}

export const getCmsMode = createSelector(
    [
        getListingConfig,
    ],
    (
        config,
    ) => {
        return getCmsModeFromConfig(config);
    }
);

export const isCurrentRouterState = (path) => {
    return createSelector([getCurrentRouteName], (currentRouteName) => {
      if (!currentRouteName) {
        return false;
      }
      return path === currentRouteName
    });
}

export const getFeeDisplayOption = createSelector(
    [
        getCheckoutConfig,
    ],
    (
        checkoutConfig,
    ) => {
        const options = [TICKET_FEE_SEPARATE, TICKET_PRICE_ONLY];
        if (checkoutConfig.feeDisplayOption && options.indexOf(+checkoutConfig.feeDisplayOption) !== -1) {
            return +checkoutConfig.feeDisplayOption;
        }
        return TICKET_FEE_SEPARATE; //default
    }
);

export const shouldPinEmbeddedCart = createSelector(
    [
        getIsEmbed,
        getEmbedCartBehavior,
    ],
    (
        isEmbed,
        embedCartBehavior
    ) => {
        return isEmbed &&
            [EMBED_CART_BEHAVIOR_PIN_TO_TOP, EMBED_CART_BEHAVIOR_PIN_TO_BOTTOM].includes(embedCartBehavior);
    }
);

export const shouldHideEmbeddedCart = createSelector(
    [
        getIsEmbed,
        getEmbedCartBehavior,
    ],
    (
        isEmbed,
        embedCartBehavior
    ) => {
        return isEmbed &&
            [EMBED_CART_BEHAVIOR_HIDE].includes(embedCartBehavior);
    }
);

export const showContinueCheckoutBanner = createSelector(
    [
        reservationCompleted,
        overlay,
        hasReservation,
        getCaptureMethod,
        getIsDynamicListing,
    ],
    (
        reservationIsCompleted,
        overlayShown,
        doesHaveReservation,
        captureMethod,
        isDynamic,
    ) => {
        if (
            !isDynamic && (
                reservationIsCompleted ||
                isCaptureMethodKioskMode(captureMethod) ||
                overlayShown ||
                !doesHaveReservation
            )
        ) {
            return false;
        }
        return true;
    }
);

export const isRegistrationFieldsFilledAndValidated = createSelector(
    [
      state => state.member,
      areAllQuestionsCompleted,
      state => state.ui?.checkoutConfig,
      getCurrentCheckoutStep
    ],
    (member, areQuestionsCompleted, checkoutConfig, currentStep) => {
        const isKiosk =  checkoutConfig?.captureMethod == 13;
        const hasMemberErrors = !!Object.values(member.errors || {}).filter(Boolean).length;

        const questionsCompleted = !currentStep.components.find(component => component.name === "questions") || areQuestionsCompleted;

        let memberInfoFilled = false;
        if(isKiosk) {
            memberInfoFilled =
              (!checkoutConfig?.kioskOptions?.customerNameRequired || !!member?.name) &&
              (!checkoutConfig?.kioskOptions?.customerEmailRequired || !!member?.email);
        } else {
            memberInfoFilled = !!member.name && !!member?.email;
        }
        return memberInfoFilled && questionsCompleted && !hasMemberErrors;
    }
)

// Extandable, same validation can be applied to Payment step if needed.
export const isSubmitStepButtonDisabled = createSelector(
    [getCurrentCheckoutStep, isRegistrationFieldsFilledAndValidated],
    (currentStep, isRegistrationCompleted) => {
        if (currentStep.title === 'Registration' || currentStep.title === 'Additional Info') {
            return !isRegistrationCompleted;
        }
    }
)

export const selectEventNameFromListingConfig = createSelector(
  getListingConfig,
  (config) => config?.listings?.title
);

export const getLoadedListingSlug = createSelector(
  [getUIState, getIsDynamicListing, getDynamicListingSlug],
  (uiState, isDynamic, dynamicListingSlug) => isDynamic ? dynamicListingSlug : uiState.listingSlug
);

export const getShouldUseThemeDark = createSelector(
  [getUIState],
  (uiState) => {
    return uiState.uiConfig.styles
        && uiState.uiConfig.styles["CONTENT__reverse-text"]
        && uiState.uiConfig.styles["FULLSCREEN"];
  }
);
